import { EasingType, BezierHandle } from '../../core/types/animation.types';

// Bezier curve calculation
function cubicBezier(t: number, p0: number, p1: number, p2: number, p3: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  const mt = 1 - t;
  const mt2 = mt * mt;
  const mt3 = mt2 * mt;
  return mt3 * p0 + 3 * mt2 * t * p1 + 3 * mt * t2 * p2 + t3 * p3;
}

// Solve for t given x using Newton-Raphson
function solveCubicBezierX(x: number, x1: number, x2: number, epsilon = 0.0001): number {
  let t = x;
  for (let i = 0; i < 8; i++) {
    const currentX = cubicBezier(t, 0, x1, x2, 1) - x;
    if (Math.abs(currentX) < epsilon) return t;
    const derivative = 3 * (1 - t) * (1 - t) * x1 + 6 * (1 - t) * t * (x2 - x1) + 3 * t * t * (1 - x2);
    if (Math.abs(derivative) < epsilon) break;
    t -= currentX / derivative;
  }
  return t;
}

export function customBezierEasing(
  t: number,
  handles: { in: BezierHandle; out: BezierHandle }
): number {
  const solvedT = solveCubicBezierX(t, handles.out.x, handles.in.x);
  return cubicBezier(solvedT, 0, handles.out.y, handles.in.y, 1);
}

function bounceOut(t: number): number {
  const n1 = 7.5625;
  const d1 = 2.75;

  if (t < 1 / d1) {
    return n1 * t * t;
  } else if (t < 2 / d1) {
    return n1 * (t -= 1.5 / d1) * t + 0.75;
  } else if (t < 2.5 / d1) {
    return n1 * (t -= 2.25 / d1) * t + 0.9375;
  } else {
    return n1 * (t -= 2.625 / d1) * t + 0.984375;
  }
}

export function applyEasing(
  t: number,
  easing: EasingType,
  bezierHandles?: { in: BezierHandle; out: BezierHandle }
): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;

  switch (easing) {
    // Linear
    case 'linear':
      return t;

    // Standard CSS-like easings
    case 'ease-in':
      return t * t * t;
    case 'ease-out':
      return 1 - Math.pow(1 - t, 3);
    case 'ease-in-out':
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    // Quadratic
    case 'ease-in-quad':
      return t * t;
    case 'ease-out-quad':
      return 1 - (1 - t) * (1 - t);
    case 'ease-in-out-quad':
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

    // Cubic
    case 'ease-in-cubic':
      return t * t * t;
    case 'ease-out-cubic':
      return 1 - Math.pow(1 - t, 3);
    case 'ease-in-out-cubic':
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    // Sine
    case 'ease-in-sine':
      return 1 - Math.cos((t * Math.PI) / 2);
    case 'ease-out-sine':
      return Math.sin((t * Math.PI) / 2);
    case 'ease-in-out-sine':
      return -(Math.cos(Math.PI * t) - 1) / 2;

    // Back (overshooting)
    case 'ease-in-back': {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return c3 * t * t * t - c1 * t * t;
    }
    case 'ease-out-back': {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    }
    case 'ease-in-out-back': {
      const c1 = 1.70158;
      const c2 = c1 * 1.525;
      return t < 0.5
        ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
        : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2;
    }

    // Elastic
    case 'ease-in-elastic': {
      const c4 = (2 * Math.PI) / 3;
      return t === 0
        ? 0
        : t === 1
        ? 1
        : -Math.pow(2, 10 * t - 10) * Math.sin((t * 10 - 10.75) * c4);
    }
    case 'ease-out-elastic': {
      const c4 = (2 * Math.PI) / 3;
      return t === 0
        ? 0
        : t === 1
        ? 1
        : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
    }
    case 'ease-in-out-elastic': {
      const c5 = (2 * Math.PI) / 4.5;
      return t === 0
        ? 0
        : t === 1
        ? 1
        : t < 0.5
        ? -(Math.pow(2, 20 * t - 10) * Math.sin((20 * t - 11.125) * c5)) / 2
        : (Math.pow(2, -20 * t + 10) * Math.sin((20 * t - 11.125) * c5)) / 2 + 1;
    }

    // Bounce
    case 'ease-in-bounce':
      return 1 - bounceOut(1 - t);
    case 'ease-out-bounce':
      return bounceOut(t);
    case 'ease-in-out-bounce':
      return t < 0.5
        ? (1 - bounceOut(1 - 2 * t)) / 2
        : (1 + bounceOut(2 * t - 1)) / 2;

    // Custom bezier
    case 'custom':
      if (bezierHandles) {
        return customBezierEasing(t, bezierHandles);
      }
      return t;

    default:
      return t;
  }
}

// Generate curve preview points for visualization
export function generateEasingCurve(
  easing: EasingType,
  bezierHandles?: { in: BezierHandle; out: BezierHandle },
  steps = 50
): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    points.push({
      x: t,
      y: applyEasing(t, easing, bezierHandles),
    });
  }
  return points;
}
